import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSimpleUserProfile } from "@/hooks/useSimpleUserProfile";
import { useSimpleConnectionStatus } from "@/hooks/useSimpleConnectionStatus";
import type { SimpleConnectionStatus } from "@/hooks/useSimpleConnectionStatus";
import { useConnectionActions } from "@/hooks/connections/useConnectionActions";
import { useProfileViewTracker } from "@/hooks/useProfileViewTracker";
import { useBlockUser } from "@/hooks/useBlockUser";
import { debugLogger } from '@/utils/debugLogger';
import { UserData } from "@/types/user";
import { Profile } from "@/types/profile";
import { chatDataService } from '@/core/services/ChatDataService';
import { toast } from 'sonner';

import ProfileHeaderOptionB from "@/components/profile/ProfileHeaderOptionB";
import EnhancedProfileDetails from "@/components/profile/EnhancedProfileDetails";
import { ProfileBioCard } from "@/components/profile/sections/ProfileBioCard";
import { ProfileCompatibilityCard } from "@/components/profile/sections/ProfileCompatibilityCard";
import { ProfileBioScoreCard } from "@/components/profile/sections/ProfileBioScoreCard";
import { ProfileTopicsCard } from "@/components/profile/sections/ProfileTopicsCard";
import RestrictedAccessMessage from "@/components/profile/RestrictedAccessMessage";
import NotFound from "@/pages/NotFound";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Helper function to validate if string is UUID
const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

// Helper function to convert UserData to Profile
const mapUserDataToProfile = (userData: UserData): Profile => {
  return {
    id: userData.id,
    username: userData.username,
    bio: userData.bio || '',
    age: userData.age,
    gender: userData.gender || '',
    orientation: userData.orientation || '',
    occupation: userData.occupation || '',
    education: userData.education || '',
    state_code: userData.state_code,
    city: userData.city || '',
    income: userData.income || '',
    interests: userData.interests,
    relationship_type: userData.relationship_type,
    seeking_casual_only: userData.seeking_casual_only,
    share_income: userData.share_income || false,
    main_photo_url: userData.main_photo_url || '',
    verification_level: userData.verification_level || 'basic',
    sponsorship_value: userData.sponsorship_value,
    birth_date: undefined,
    latitude: undefined,
    longitude: undefined,
    joinedDate: userData.joinedDate,
    role: userData.role,
    distance: undefined,
    photos: userData.photos || [],
    profile_photos: userData.photos,
    avatarUrl: undefined,
    displayName: userData.username,
    hidden: userData.hidden,
    is_online: undefined,
    last_seen: undefined,
    connection_status: undefined,
    connection_id: undefined,
    connection_created_at: undefined,
    notificationPreferences: undefined,
    created_at: undefined,
    last_login: undefined,
    onboarding_completed: userData.onboarding_completed,
    // Physical characteristics
    height: userData.height,
    body_type: userData.body_type,
    hair_color: userData.hair_color,
    eye_color: userData.eye_color,
    smoker: userData.smoker,
    drinker: userData.drinker,
    piercings: userData.piercings,
    tattoos: userData.tattoos,
    // Relationship preferences
    travel_availability: userData.travel_availability,
    children: userData.children,
    marital_status: userData.marital_status,
    sigilo_presenca: userData.sigilo_presenca,
    // Bio AI analysis fields
    bio_score: userData.bio_score,
    bio_topics: userData.bio_topics,
    bio_last_analyzed: userData.bio_last_analyzed
  };
};

const UserProfile = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user, profileData } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState<SimpleConnectionStatus>('none');
  const [showUnmatchDialog, setShowUnmatchDialog] = useState(false);
  const [bypassRoleCheck, setBypassRoleCheck] = useState(false);

  // Validação de parâmetro de entrada
  if (!username) {
    debugLogger.error('UserProfile: No username provided in URL params');
    return <NotFound />;
  }

  // Verificar se o parâmetro é UUID (erro comum)
  if (isUUID(username)) {
    debugLogger.error('UserProfile: Username parameter appears to be a UUID:', username);
    return <NotFound />;
  }

  debugLogger.debug('UserProfile: Loading profile for username:', username);

  const { profile, loading: profileLoading, error } = useSimpleUserProfile(username, user?.id, bypassRoleCheck);
  const { getConnectionStatus, likeProfile, unlikeProfile, invalidateCache, loading: statusLoading, likeLimits } = useSimpleConnectionStatus();
  const { handleUnmatch } = useConnectionActions();
  
  // Block status check using profile.id
  const { isBlockedBy, checkBlockStatus } = useBlockUser(profile?.id);

  // Profile view tracking - apenas quando temos o profile.id válido
  useProfileViewTracker(profile?.id);

  // Check block status when profile loads
  useEffect(() => {
    if (profile?.id && user?.id) {
      checkBlockStatus();
    }
  }, [profile?.id, user?.id, checkBlockStatus]);

  useEffect(() => {
    if (!profileLoading && !statusLoading) {
      setIsLoading(false);
    }
  }, [profileLoading, statusLoading]);

  useEffect(() => {
    const fetchConnectionStatus = async () => {
      if (profile?.id && user?.id && profile.id !== user.id) {
        debugLogger.debug('UserProfile: Fetching connection status between', user.id, 'and', profile.id);
        const status = await getConnectionStatus(profile.id);
        setConnectionStatus(status);
      }
    };

    fetchConnectionStatus();
  }, [profile?.id, user?.id, getConnectionStatus]);

  const refreshStatus = async () => {
    if (profile?.id) {
      invalidateCache(profile.id);
      const newStatus = await getConnectionStatus(profile.id);
      setConnectionStatus(newStatus);
    }
  };

  const handleAction = async (actionType: 'like' | 'unlike' | 'message' | 'unmatch') => {
    if (!profile) {
      debugLogger.error('UserProfile: No profile available for action:', actionType);
      return;
    }

    debugLogger.debug('UserProfile: Action triggered', { actionType, profileId: profile.id, username: profile.username });

    if (actionType === 'like') {
      const success = await likeProfile(profile.id);
      if (success) {
        debugLogger.debug('UserProfile: Like successful, refreshing status');
        await refreshStatus();
      }
    } else if (actionType === 'unlike') {
      const success = await unlikeProfile(profile.id);
      if (success) {
        debugLogger.debug('UserProfile: Unlike successful, refreshing status');
        await refreshStatus();
      }
    } else if (actionType === 'message') {
      debugLogger.debug('UserProfile: Navigating to chat with:', profile.username);
      navigate(`/main/chat/${profile.username}`);
    } else if (actionType === 'unmatch') {
      setShowUnmatchDialog(true);
    }
  };

  const confirmUnmatch = async () => {
    if (!profile || !user) return;

    try {
      debugLogger.debug('UserProfile: Proceeding with unmatch for:', profile.username);
      
      // Perform unmatch
      const unmatchSuccess = await handleUnmatch(profile.id);
      
      if (unmatchSuccess) {
        // Try to find and soft delete conversation
        try {
          const conversationId = await chatDataService.getConversationByUsername(user.id, profile.username);
          if (conversationId) {
            const deleteSuccess = await chatDataService.deleteConversation(conversationId, user.id);
            if (deleteSuccess) {
              debugLogger.debug('UserProfile: Conversation soft deleted after unmatch');
            }
          }
        } catch (convError) {
          debugLogger.warn('UserProfile: Error handling conversation after unmatch:', convError);
          // Don't fail the unmatch if conversation handling fails
        }

        toast.success('Conexão desfeita com sucesso');
        await refreshStatus();
      } else {
        toast.error('Erro ao desfazer conexão');
      }
    } catch (error) {
      debugLogger.error('UserProfile: Error during unmatch process:', error);
      toast.error('Erro ao desfazer conexão');
    } finally {
      setShowUnmatchDialog(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-luxury-gold border-solid"></div>
      </div>
    );
  }

  // Handle blocked by user - they shouldn't see this profile
  if (isBlockedBy) {
    debugLogger.info('UserProfile: Access blocked - user is blocked by profile owner', { username });
    return (
      <RestrictedAccessMessage />
    );
  }

  // Handle role incompatibility error - fluxo esperado, usar INFO
  if (error === 'ROLE_INCOMPATIBLE' && !bypassRoleCheck) {
    debugLogger.info('UserProfile: Access restricted due to role incompatibility', { username });
    return (
      <RestrictedAccessMessage 
        onViewAnyway={() => setBypassRoleCheck(true)} 
      />
    );
  }

  // Erros reais ou perfil não encontrado
  if (error || !profile) {
    debugLogger.error('UserProfile: Profile not found or technical error', { error, username });
    return <NotFound />;
  }

  // Convert UserData to Profile for components that expect it
  const viewedProfileData = mapUserDataToProfile(profile);

  debugLogger.debug('UserProfile: Successfully loaded profile for', profile.username);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900">
        <div className="max-w-6xl mx-auto px-4 py-6 md:py-8 pb-24 md:pb-6">
          {/* Two-column responsive grid - mobile uses consistent space-y-4, desktop uses gap-8 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            
            {/* Left Column - Photo + Info + Actions (STICKY on desktop) */}
            <div className="md:sticky md:top-6 md:self-start space-y-4">
              <ProfileHeaderOptionB
                profile={viewedProfileData}
                connectionStatus={connectionStatus}
                onAction={handleAction}
                canLike={likeLimits.canLike}
                likeLimits={likeLimits}
              />
              
              {/* Desktop only: Compatibility and Topics in left column */}
              <div className="hidden md:block space-y-4">
                <ProfileCompatibilityCard 
                  profile={viewedProfileData}
                  currentUserProfile={profileData ? {
                    ...profileData,
                    bio: profileData.bio || '',
                    photos: [],
                    seeking_casual_only: (profileData as any).seeking_casual_only
                  } as Profile : undefined}
                />
                {/* ProfileTopicsCard hidden from UI but data remains for AI compatibility */}
              </div>
            </div>

            {/* Right Column - Bio, Details */}
            <div className="space-y-4">
              {/* Mobile only: Bio Score (Análise da Bio) comes first */}
              <div className="md:hidden">
                <ProfileBioScoreCard profile={viewedProfileData} />
              </div>
              
              <ProfileBioCard profile={viewedProfileData} />
              
              {/* Mobile only: Compatibility and Topics here */}
              <div className="md:hidden space-y-4">
                <ProfileCompatibilityCard 
                  profile={viewedProfileData}
                  currentUserProfile={profileData ? {
                    ...profileData,
                    bio: profileData.bio || '',
                    photos: [],
                    seeking_casual_only: (profileData as any).seeking_casual_only
                  } as Profile : undefined}
                />
                {/* ProfileTopicsCard hidden from UI but data remains for AI compatibility */}
              </div>
              
              {/* Desktop only: Bio Score after Bio Card */}
              <div className="hidden md:block">
                <ProfileBioScoreCard profile={viewedProfileData} />
              </div>
              <EnhancedProfileDetails 
                profile={viewedProfileData} 
                currentUserInterests={profileData?.interests || []}
                currentUserProfile={profileData ? {
                  ...profileData,
                  bio: profileData.bio || '',
                  photos: [],
                  seeking_casual_only: (profileData as any).seeking_casual_only
                } as Profile : undefined}
              />
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={showUnmatchDialog} onOpenChange={setShowUnmatchDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desfazer conexão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja desfazer esta conexão? Isso também excluirá a conversa entre vocês se houver.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUnmatch} className="bg-red-600 hover:bg-red-700">
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default UserProfile;
